<svelte:options
  customElement={{
    tag: "oc-progress-bar-v1",
    /*                       */
    extend: window.__components.extend(),
    props: {
      inverted: { type: "Boolean" },
      max: { type: "Number" },
      min: { type: "Number" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      size: { type: "String" },
      steps: { type: "Number" },
      value: { type: "Number" },
      variant: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import type { Props } from "./ProgressBarV1.types.js";
  import DefaultStyles from "../../../common/components/DefaultStyles.svelte";

  let {
    value,
    variant = "primary",
    inverted,
    min = 0,
    max = 1,
    ocAriaLabel = undefined,
    steps = 1,
    size = "100",
  }: Props = $props();

  let allProps = OC.$allProps();

  let mustache = $state<typeof import("@otto-ec/global-resources/mustache").mustache>();
  import("@otto-ec/global-resources/mustache").then(({ mustache: m }) => (mustache = m));

  let ariaLabel = $derived(ocAriaLabel ? mustache?.render(ocAriaLabel, allProps) : undefined);
</script>

{#snippet items()}
  <!-- eslint-disable-next-line @typescript-eslint/no-unused-vars -->
  {#each Array(steps) as _, key (key)}
    <div></div>
  {/each}
{/snippet}

<div
  class={`progress progress--size-${size} progress--variant-${variant}`}
  class:progress--inverted={inverted}
  role="progressbar"
  aria-valuenow={value}
  aria-valuemin={min}
  aria-valuemax={max}
  aria-label={ariaLabel}
  aria-hidden={!ariaLabel}
  style:--progress={(value - min) / (max - min)}
>
  <div class="track">
    {@render items()}
  </div>

  {#if variant === "warning"}
    <div class="border">
      {@render items()}
    </div>
  {/if}

  <div class="active">
    {@render items()}
  </div>
</div>

<DefaultStyles />

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  :host {
    display: block;
  }

  .progress {
    position: relative;
    height: var(--height);
    --track-color: #{tokens.$oc-component-progress-bar-track-color};
    --border-width: 0px;
    --bar-gap: #{tokens.$oc-component-progress-bar-default-gap-x};

    &--size-50 {
      --test: blue;
      --border-radius: #{tokens.$oc-component-progress-bar-50-border-radius};
      --steps-gap: #{tokens.$oc-component-progress-bar-stepped-50-gap-x};
      --stop-indicator-radius: #{tokens.$oc-component-progress-bar-stop-indicator-50-border-radius};
      --stop-indicator-size: #{tokens.$oc-component-progress-bar-stop-indicator-50-size};
      --height: #{tokens.$oc-component-progress-bar-50-height};
    }

    &--size-100 {
      --border-radius: #{tokens.$oc-component-progress-bar-100-border-radius};
      --steps-gap: #{tokens.$oc-component-progress-bar-stepped-100-gap-x};
      --stop-indicator-radius: #{tokens.$oc-component-progress-bar-stop-indicator-100-border-radius};
      --stop-indicator-size: #{tokens.$oc-component-progress-bar-stop-indicator-100-size};
      --height: #{tokens.$oc-component-progress-bar-100-height};
    }

    &--variant-primary {
      --active-color: #{tokens.$oc-component-progress-bar-primary-active-indicator-color};
      --stop-indicator-color: #{tokens.$oc-component-progress-bar-primary-stop-indicator-color};
    }

    &--variant-secondary {
      --active-color: #{tokens.$oc-component-progress-bar-secondary-active-indicator-color};
      --stop-indicator-color: #{tokens.$oc-component-progress-bar-secondary-stop-indicator-color};
    }

    &--variant-success {
      --active-color: #{tokens.$oc-component-progress-bar-success-active-indicator-color};
      --stop-indicator-color: #{tokens.$oc-component-progress-bar-success-stop-indicator-color};
    }

    &--variant-warning {
      --active-color: #{tokens.$oc-component-progress-bar-warning-active-indicator-color};
      --border-color: #{tokens.$oc-component-progress-bar-warning-border-color};
      --border-width: #{tokens.$oc-component-progress-bar-warning-border-width};
      --stop-indicator-color: #{tokens.$oc-component-progress-bar-warning-stop-indicator-color};
    }

    &--variant-error {
      --active-color: #{tokens.$oc-component-progress-bar-error-active-indicator-color};
      --stop-indicator-color: #{tokens.$oc-component-progress-bar-error-stop-indicator-color};
    }

    &--variant-hint {
      --active-color: #{tokens.$oc-component-progress-bar-hint-active-indicator-color};
      --stop-indicator-color: #{tokens.$oc-component-progress-bar-hint-stop-indicator-color};
    }

    &--inverted {
      --track-color: #{tokens.$oc-component-progress-bar-track-color-inverted};
    }
  }

  .track,
  .border,
  .active {
    position: absolute;
    inset: 0;
    width: 100%;
    display: flex;
    gap: var(--steps-gap);
    /*                                                                                          */
    --p: calc((100% + var(--bar-gap)) * var(--progress) - var(--bar-gap));

    @media (prefers-reduced-motion: no-preference) {
      transition: mask-size
        tokens.$oc-component-progress-bar-transition-show-active-indicator-duration
        tokens.$oc-component-progress-bar-transition-show-active-indicator-easing;
    }

    > * {
      width: 100%;
      border-radius: var(--border-radius);
      background: var(--background-color);
    }
  }

  .track {
    --background-color: var(--track-color);
  }

  .border {
    --background-color: var(--border-color);
  }

  .active {
    --background-color: var(--active-color);

    > * {
      margin: var(--border-width);
    }
  }

  .track {
    mask: linear-gradient(to right, #000, #000) 100% 0 / calc(100% - var(--p) - var(--bar-gap))
      no-repeat;
  }

  .border {
    mask: linear-gradient(to right, #000, #000) 0 0 / var(--p) no-repeat;
  }

  .active {
    mask: linear-gradient(to right, #000, #000) 0 0 / calc(var(--p) - var(--border-width)) no-repeat;
  }

  .track {
    --spacing: calc((var(--height) - var(--stop-indicator-size)) / 2);

    &:before,
    &:after {
      box-sizing: border-box;
      content: "";
      position: absolute;
      top: var(--spacing);
      width: var(--stop-indicator-size);
      height: var(--stop-indicator-size);
      background: var(--stop-indicator-color);
      border: 1px solid var(--border-color, --stop-indicator-color);
      border-radius: var(--stop-indicator-radius);
    }

    &:before {
      left: var(--spacing);
    }

    &:after {
      right: var(--spacing);
    }
  }
</style>
